import React, { useState, useRef, useContext, useEffect } from 'react';
import { Grid, Button, Box, Typography } from '@mui/material';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import { useDispatch } from 'react-redux';
import { ADD_SKILL } from '../../Reducers/Actions';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import FormInput from '../../SystemDesign/FormInput';
import CardStepper from '../../SystemDesign/CardStepper';
import FormSelect from '../../SystemDesign/FormSelect';
import { useCreateSkillMutation, useGetUnitsQuery, useGetSkillsQuery, useAddSkillToUnitsMutation } from '../../services/SkillsManagement';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import MuiDialog from '../../SystemDesign/MuiDialog';
import AddIcon from '@mui/icons-material/Add';
import ColoredSelector from '../../SystemDesign/ColoredSelector';
import { PrimaryButton } from '../../SystemDesign/Styles';

const AddSkill = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // Use SnackBar context for displaying messages
  const { setOpenSnackBar, setInfoAlert } = useContext(SnackBarContext);
  
  // State for dialog and form
  const [isOpen, setIsOpen] = useState(false);
  const formikRef = useRef(null);
  
  // Fetch units for dropdown
  // Provide an empty object with required parameters to prevent the "queryArg is undefined" error
  const { data: unitsData, error: unitsError, isLoading: unitsLoading } = useGetUnitsQuery({
    pagination: JSON.stringify({ currentPage: 1, rowsPerPage: 10 }),
    sorting: JSON.stringify({}),
    search: "",
  });
  const units = unitsData?.data?.units || [];
  
  // RTK Query mutation hook
  const [createSkill, { isLoading }] = useCreateSkillMutation();
  const [addSkillToUnits, { isLoading: isAddingToUnits }] = useAddSkillToUnitsMutation();
  const { refetch } = useGetSkillsQuery({
    pagination: JSON.stringify({ currentPage: 1, rowsPerPage: 50 }), // Fetch more to ensure new skill appears
    sorting: JSON.stringify({})
  }, {
    refetchOnMountOrArgChange: true // Ensure refetching works properly
  });
  
  // Initial values for the form
  const initialValues = {
    // Basic Information
    code: '',
    name: '',
    description: '',
    priority: 'Medium',
    unit_id: [],
    
    // Skill Levels
    awareness_description: '',
    application_description: '',
    management_description: '',
    
    // References and Comments
    external_reference: '',
    internal_reference: '',
    comments: ''
  };
  
  // Priority options
  const priorityOptions = [
    { id: 'Low', name: 'Low', color: colors.green[400] },
    { id: 'Medium', name: 'Medium', color: colors.Orange[400] },
    { id: 'High', name: 'High', color: colors.redAccent[400] }
  ];
  
  // Stepper state
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  
  // Step labels
  const steps = ['Basic Information', 'Skill Levels', 'References & Comments'];
  
  // Form fields configuration by step
  const step1Fields = [
    { name: 'code', label: 'Skill Code', type: 'text', placeholder: 'Ex: PBED, H0101...', required: true },
    { name: 'name', label: 'Skill Name', type: 'text', placeholder: 'Ex: Work Preparation & Debrief...', required: true },
    {
      type: 'select',
      name: 'priority',
      label: 'Priority',
      required: true,
      options: priorityOptions.map(opt => ({ id: opt.id, title: opt.name })),
      disabled: false
    },
    {
      type: 'select',
      name: 'unit_id',
      label: 'Units',
      required: true,
      options: units.map(unit => ({ id: unit.unit_id, title: unit.specific_unit })),
      multiple: true,
      disabled: unitsLoading || units.length === 0
    },
    { name: 'description', label: 'Description', type: 'text', placeholder: 'Describe the skill...', required: true, multiline: true, rows: 3 }
  ];
  
  const step2Fields = [
    { name: 'awareness_description', label: 'Awareness Description', type: 'text', placeholder: 'Describe awareness level...', required: false, multiline: true, rows: 3 },
    { name: 'application_description', label: 'Skill Application Description', type: 'text', placeholder: 'Describe application level...', required: false, multiline: true, rows: 3 },
    { name: 'management_description', label: 'Management Description', type: 'text', placeholder: 'Describe management level...', required: false, multiline: true, rows: 3 }
  ];
  
  const step3Fields = [
    { name: 'external_reference', label: 'External Reference', type: 'text', placeholder: 'External documentation or resources...', required: false, multiline: true, rows: 2 },
    { name: 'internal_reference', label: 'Internal Reference', type: 'text', placeholder: 'Internal documentation or resources...', required: false, multiline: true, rows: 2 },
    { name: 'comments', label: 'Comments', type: 'text', placeholder: 'Any additional notes...', required: false, multiline: true, rows: 2 }
  ];
  
  // Validation schema
  const validationSchema = Yup.object({
    // Step 1
    code: Yup.string().required('Skill code is required'),
    name: Yup.string().required('Skill name is required'),
    description: Yup.string().required('Description is required'),
    priority: Yup.string().required('Priority is required'),
    unit_id: Yup.array().of(Yup.number().typeError('Unit must be a number')).min(1, 'Select at least one unit').required('Select at least one unit'),
    
    // Step 2
    awareness_description: Yup.string(),
    application_description: Yup.string(),
    management_description: Yup.string(),
    
    // Step 3 (not required)
    external_reference: Yup.string(),
    internal_reference: Yup.string(),
    comments: Yup.string()
  });
  
  // Get grid sizing for form fields
  function getFieldGridProps(field) {
    switch (field.name) {
      case 'code':
      case 'name':
        return { xs: 6, md: 6 };
      case 'priority':
      case 'unit_id':
        return { xs: 12, md: 12 };
      case 'description':
      case 'comments':
        return { xs: 12, md: 12 };
      case 'awareness_description':
      case 'application_description':
      case 'management_description':
        return { xs: 12, md: 12 };
      case 'external_reference':
      case 'internal_reference':
        return { xs: 12, md: 12 };
      default:
        return { xs: 12, md: 6 };
    }
  }

  // Validate step before navigation
  const validateStep = (formik, step, onSuccess) => {
    // Touch all fields in current step to trigger validation
    touchAllFieldsInStep(formik, step);
    
    // Validate after touching
    formik.validateForm().then(errors => {
      if (isStepValid(formik.values, errors, step)) {
        // If valid, execute the success callback
        onSuccess();
      } else if (step === 0) {
        // Only show error message for step 1 (which has required fields)
        setInfoAlert({
          severity: 'error',
          message: 'Please fill out all required fields before proceeding'
        });
        setOpenSnackBar(true);
      }
    });
  };
  
  // Handle step completion
  const handleComplete = (formik) => {
    const newCompleted = { ...completed };
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
    // We don't automatically submit the form here anymore to avoid double submission
  };
  
  // Custom step handlers to enforce validation
  const handleNext = () => {
    const formik = formikRef.current;
    if (formik && activeStep === 0) {
      validateStep(formik, activeStep, () => {
        setActiveStep(activeStep + 1);
      });
    } else {
      // For other steps, no validation needed
      setActiveStep(activeStep + 1);
    }
  };
  
  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };
  
  const handleStepClick = (step) => {
    const formik = formikRef.current;
    if (formik && activeStep === 0 && step > activeStep) {
      // If trying to move forward from step 1, validate
      validateStep(formik, activeStep, () => {
        setActiveStep(step);
      });
    } else {
      // For backward navigation or from other steps, allow freely
      setActiveStep(step);
    }
  };
  
  // Check if current step is valid
  const isStepValid = (values, errors, step) => {
    let stepFields;
    switch(step) {
      case 0:
        stepFields = ['code', 'name', 'description', 'priority', 'unit_id'];
        break;
      case 1:
        return true;
      case 2:
        return true;
      default:
        return false;
    }
    
    // Check if all required fields in this step are valid
    return stepFields.every(field => {
      return !errors[field];
    });
  };
  
  // Touch all fields in the current step to trigger validation
  const touchAllFieldsInStep = (formik, step) => {
    let stepFields;
    switch(step) {
      case 0:
        stepFields = ['code', 'name', 'description', 'priority', 'unit_id'];
        break;
      case 1:
        stepFields = ['awareness_description', 'application_description', 'management_description'];
        break;
      case 2:
        stepFields = ['external_reference', 'internal_reference', 'comments'];
        break;
      default:
        stepFields = [];
    }
    
    const touchedFields = {};
    stepFields.forEach(field => {
      touchedFields[field] = true;
    });
    
    formik.setTouched({ ...formik.touched, ...touchedFields }, true);
  };

  const handleSubmit = async (values, actions) => {
    try {
      // console.log('Form values being submitted:', values);
      
      // From multi-select units: use the first as base, others for duplication
      const selectedUnits = Array.isArray(values.unit_id) ? values.unit_id : [];
      const baseUnitId = Number.isFinite(parseInt(selectedUnits[0], 10)) ? parseInt(selectedUnits[0], 10) : 0;
      
      // Ensure all string fields are sent as empty strings instead of null/undefined
      const ensureString = (value) => {
        if (value === undefined || value === null) return '';
        return typeof value === 'string' ? value.trim() : String(value);
      };
      
      // Prepare the request body
      const requestBody = {
        skill_code: ensureString(values.code),
        skill_name: ensureString(values.name),
        name: ensureString(values.name),
        description: ensureString(values.description),
        priority: values.priority.toLowerCase(),
        unit_id: baseUnitId,
        awareness_description: ensureString(values.awareness_description),
        skilled_application_description: ensureString(values.application_description),
        management_description: ensureString(values.management_description),
        external_reference: ensureString(values.external_reference),
        internal_reference: ensureString(values.internal_reference),
        comments: ensureString(values.comments)
      };
      
      // console.log('Request body:', requestBody);
      
      // Make API call to create skill
      const response = await createSkill({
        body: requestBody
      }).unwrap();
      
      // console.log('API response for new skill:', response);
      
      // The skill data is nested inside the 'data' property of the response
      const skillData = response.data || {};
      const skillId = skillData.skill_id || skillData.id;
      
      // console.log('Extracted skill ID:', skillId, 'from data:', skillData);
      
      if (!skillId) {
        console.error('No skill_id in API response data:', response);
        setInfoAlert({
          severity: 'error',
          message: 'Could not create skill: No skill ID returned from server'
        });
        setOpenSnackBar(true);
        actions.setSubmitting(false);
        return;
      }
      
      // Prepare complete skill object for Redux store
      // This is crucial - we need to create a complete object that doesn't need
      // to be modified by the reducer
      
      // Find the complete unit object from our units data
      const selectedUnit = units.find(u => u.unit_id === baseUnitId || u.id === baseUnitId);
      
      
      // Create a fully hydrated unit object
      const fullUnitObject = selectedUnit ? {
        ...selectedUnit,
        unit_id: selectedUnit.unit_id || selectedUnit.id,
        id: selectedUnit.id || selectedUnit.unit_id,
        // Include discipline data if available
        discipline: selectedUnit.discipline || null
      } : { unit_id: baseUnitId };
      
      const skillForStore = {
        skill_id: skillId,
        id: skillId, // Ensure both ID formats are available
        skill_code: skillData.skill_code || values.code,
        skill_name: skillData.skill_name || values.name,
        name: skillData.name || values.name,
        description: skillData.description || values.description,
        priority: skillData.priority || values.priority.toLowerCase(),
        unit_id: baseUnitId,
        awareness_description: skillData.awareness_description || values.awareness_description || '',
        skilled_application_description: skillData.skilled_application_description || values.application_description || '',
        management_description: skillData.management_description || values.management_description || '',
        external_reference: skillData.external_reference || values.external_reference || '',
        internal_reference: skillData.internal_reference || values.internal_reference || '',
        comments: skillData.comments || values.comments || '',
        createdAt: skillData.createdAt || new Date().toISOString(),
        updatedAt: skillData.updatedAt || new Date().toISOString(),
        // Use our fully hydrated unit object with discipline data
        unit: skillData.unit || fullUnitObject
      };
      
      // console.log('BEFORE dispatch - Adding skill to Redux store:', skillForStore);
      
      // Dispatch the complete skill object to Redux
      dispatch({
        type: ADD_SKILL,
        payload: skillForStore
      });
      
      // console.log('AFTER dispatch - Added skill to Redux store with ADD_SKILL action');
      
      // If user selected multiple units, duplicate to the remaining ones
      let duplicateSummary = '';
      // Remaining selected units after the first are duplication targets
      const unitIds = [...new Set((selectedUnits.slice(1) || []).map((v) => parseInt(v, 10)).filter((id) => Number.isFinite(id) && id !== baseUnitId))];
      if (unitIds.length > 0) {
        try {
          const dupResp = await addSkillToUnits({ body: { skillId, unitIds } }).unwrap();
          const data = dupResp?.data || dupResp || {};
          const created = data.createdCount ?? 0;
          const skippedExisting = data.skippedExisting ?? 0;
          const skippedSameUnit = data.skippedSameUnit ?? 0;
          const failed = data.failedCount ?? 0;
          duplicateSummary = ` | Duplicated: ${created} created, ${skippedExisting} existed, ${skippedSameUnit} same unit, ${failed} failed`;
        } catch (dupErr) {
          console.error('Error duplicating skill to units:', dupErr);
          duplicateSummary = ' | Duplication failed';
        }
      }
      
      // Trigger a refetch of the skills list
      // console.log('Refetching skills list...');
      refetch();
      
      // Reset form and show success message with duplication summary if any
      actions.resetForm();
      setInfoAlert({
        severity: 'success',
        message: `Skill "${values.code} - ${values.name}" successfully created${duplicateSummary}`
      });
      setOpenSnackBar(true);
      setIsOpen(false);
    } catch (error) {
      console.error('Error creating skill:', error);
      
      // Better error message extraction
      let errorMessage = 'Failed to create skill';
      
      if (error.data) {
        console.error('Error data:', error.data);
        errorMessage += ': ' + JSON.stringify(error.data);
      } else if (error.message) {
        errorMessage += ': ' + error.message;
      } else if (typeof error === 'string') {
        errorMessage += ': ' + error;
      } else if (typeof error === 'object') {
        errorMessage += ': ' + JSON.stringify(error);
      }
      
      setInfoAlert({
        severity: 'error',
        message: errorMessage
      });
      setOpenSnackBar(true);
    }
    actions.setSubmitting(false);
  };
  
  // We don't need this effect anymore because we close the dialog 
  // directly in the handleSubmit function after success or error

  // Create the dialog content with form and stepper
  const dialogContent = (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formik) => {
        const { values, errors, touched } = formik;
        return (
          <Form id="add-skill-form">
            <CardStepper
              steps={steps}
              activeStep={activeStep}
              setActiveStep={handleStepClick}
              completed={completed}
              setCompleted={setCompleted}
              handleComplete={() => handleComplete(formik)}
              handleNext={handleNext}
              handleBack={handleBack}
            >
              {activeStep === 0 && (
                <Box>
                  <Grid container spacing={2}>
                    {step1Fields.map((field, index) => (
                      <Grid item size={{...getFieldGridProps(field)}} key={index}>
                        {field.type === 'select' ? (
                          field.name === 'unit_id' ? (
                            <FormSelect
                              name={field.name}
                              label={field.label}
                              multiple
                              options={units.map(u => ({ id: u.unit_id, title: u.specific_unit }))}
                              value={formik.values.unit_id}
                              onChange={(e) => {
                                const selected = e.target.value || [];
                                formik.setFieldValue('unit_id', selected);
                              }}
                              error={formik.touched.unit_id && Boolean(formik.errors.unit_id)}
                              helperText={formik.touched.unit_id && formik.errors.unit_id}
                              disabled={unitsLoading || units.length === 0}
                            />
                          ) : (
                            <ColoredSelector
                              options={priorityOptions}
                              value={formik.values.priority}
                              onChange={(value) => formik.setFieldValue('priority', value)}
                              placeholder="Select priority"
                              label="Priority"
                              colorKey="color"
                              valueKey="id"
                              labelKey="name"
                              searchable={false}
                              required={true}
                              error={formik.touched.priority && Boolean(formik.errors.priority)}
                              helperText={formik.touched.priority && formik.errors.priority}
                            />
                          )
                        ) : (
                          <FormInput
                            {...field}
                            value={formik.values[field.name]}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                            helperText={formik.touched[field.name] && formik.errors[field.name]}
                          />
                        )}
                      </Grid>
                    ))}
                  </Grid>

                </Box>
              )}
              
              {activeStep === 1 && (
                <Box>
                  <Grid container spacing={2}>
                    {step2Fields.map((field, index) => (
                      <Grid item size={{...getFieldGridProps(field)}} key={index}>
                        <FormInput
                          {...field}
                          value={formik.values[field.name]}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                          helperText={formik.touched[field.name] && formik.errors[field.name]}
                        />
                      </Grid>
                    ))}
                  </Grid>

                </Box>
              )}
              
              {activeStep === 2 && (
                <Box>
                  <Grid container spacing={2}>
                    {step3Fields.map((field, index) => (
                      <Grid item size={{...getFieldGridProps(field)}} key={index}>
                        <FormInput
                          {...field}
                          value={formik.values[field.name]}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                          helperText={formik.touched[field.name] && formik.errors[field.name]}
                        />
                      </Grid>
                    ))}
                  </Grid>

                </Box>
              )}
            </CardStepper>
          </Form>
        );
      }}
    </Formik>
  );

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: "2rem" }}>
      {/* Add Skill button */}
      <Button 
        startIcon={<AddIcon />}
        onClick={() => setIsOpen(true)}
         variant="contained"
            color="primary"
      >
        Add Skill
      </Button>

      {/* Dialog that opens when the button is clicked */}
      <MuiDialog
        icon={<AddIcon />}
        title="Create New Skill"
        subtitle="Add a new skill with detailed information"
        content={dialogContent}
        existAction={activeStep === steps.length - 1}
        actionTitle="Create Skill"
        maxWidth="md"
        actionFunction={() => {
          // Validate current step before submission
          const formik = formikRef.current;
          if (formik) {
            if (activeStep === steps.length - 1) {
             
            } else {
              // For step 1, validate before proceeding
              validateStep(formik, activeStep, () => {
                handleComplete(formik);
              });
            }
          }
        }}
        actionType="submit"
        formId="add-skill-form"
        open={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </div>
  );
};

export default AddSkill;